import { has, Permission, type Issue, type Me } from "@gjwtrack/domain";
import type { FieldDef, SchemaIndex } from "@gjwtrack/query-language";

export function issueKey(schema: SchemaIndex | null, issue: Pick<Issue, "projectId" | "number">): string {
  const p = schema?.projectsById.get(issue.projectId);
  return `${p?.key ?? issue.projectId}-${issue.number}`;
}

export function parseIssueKey(key: string): { projectKey: string; number: number } | null {
  const m = /^([A-Za-z][A-Za-z0-9_]*)-(\d+)$/.exec(key.trim());
  if (!m) return null;
  return { projectKey: m[1]!, number: Number(m[2]) };
}

export function userName(schema: SchemaIndex | null, id: number): string {
  return schema?.usersById.get(id)?.name ?? `user ${id}`;
}

export function can(me: Me | null, projectId: number, p: Permission): boolean {
  if (!me) return false;
  if (me.isAdmin) return true;
  const bits = me.projects[String(projectId)];
  return bits !== undefined && has(BigInt(bits), p);
}

export { Permission };

const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

/** Short form for dense lists: "5m", "3h", "2d", else a short date. */
export function relTime(ms: number, now = Date.now()): string {
  const d = now - ms;
  if (d < MIN) return "now";
  if (d < HOUR) return `${Math.floor(d / MIN)}m`;
  if (d < DAY) return `${Math.floor(d / HOUR)}h`;
  if (d < 14 * DAY) return `${Math.floor(d / DAY)}d`;
  return new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric", ...(d > 300 * DAY ? { year: "numeric" } : {}) });
}

/** YouTrack's long form, with "just now" for the first minute: "just now", "about 4 hours ago", "3 days ago". */
export function relTimeAgo(ms: number, now = Date.now()): string {
  const d = Math.max(0, now - ms);
  const plural = (n: number, unit: string): string => `${n} ${unit}${n === 1 ? "" : "s"} ago`;
  if (d < MIN) return "just now";
  if (d < 45 * MIN) return plural(Math.round(d / MIN), "minute");
  if (d < 90 * MIN) return "about 1 hour ago";
  if (d < DAY) return `about ${plural(Math.round(d / HOUR), "hour")}`;
  if (d < 30 * DAY) return plural(Math.round(d / DAY), "day");
  if (d < 365 * DAY) return `about ${plural(Math.round(d / (30 * DAY)), "month")}`;
  return `over ${plural(Math.floor(d / (365 * DAY)), "year")}`;
}

/** Initials for avatars and value badges: "Dev Chen" → "DC", "Deploy to Prod" → "D". */
export function initials(text: string, max = 2): string {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (max === 1) return words[0]![0]!.toUpperCase();
  return words.slice(0, max).map((w) => w[0]!.toUpperCase()).join("");
}

/** A stable colour slot (1..8) for a name, for user avatars. */
export function hashSlot(text: string): number {
  let h = 0;
  for (const ch of text) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return (h % 8) + 1;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** YouTrack's table-view timestamp: "8 Sep 2026 15:01". */
export function tableTime(ms: number): string {
  const d = new Date(ms);
  const two = (n: number): string => String(n).padStart(2, "0");
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()} ${two(d.getHours())}:${two(d.getMinutes())}`;
}

/** YouTrack's rounded counts next to titles and saved searches: 41152 → "41k+". */
export function compactCount(n: number): string {
  return n >= 1000 ? `${Math.floor(n / 1000)}k+` : String(n);
}

export function absTime(ms: number): string {
  return new Date(ms).toLocaleString();
}

export function periodText(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h && m ? `${h}h ${m}m` : h ? `${h}h` : `${m}m`;
}

/** One field value as text for lists and read-only views. */
export function fieldValueText(schema: SchemaIndex, def: FieldDef, v: number | string): string {
  switch (def.kind) {
    case "enum":
    case "state":
    case "version":
    case "build":
    case "owned":
      return typeof v === "number" ? (schema.elementsById.get(v)?.name ?? `#${v}`) : String(v);
    case "user":
      return typeof v === "number" ? userName(schema, v) : String(v);
    case "group":
      return `group ${v}`;
    case "date":
      return typeof v === "number" ? new Date(v).toLocaleDateString(undefined, { timeZone: "UTC" }) : String(v);
    case "datetime":
      return typeof v === "number" ? new Date(v).toLocaleString() : String(v);
    case "period":
      return typeof v === "number" ? periodText(v) : String(v);
    default:
      return String(v);
  }
}

/** Palette slot (1…8) of an option-set value; 0 = no colour. The `chip-N` classes hold the tokens for it. */
export function chipSlot(schema: SchemaIndex, v: number | string): number {
  if (typeof v !== "number") return 0;
  const slot = schema.elementsById.get(v)?.colorId ?? 0;
  return slot > 0 ? ((slot - 1) % 8) + 1 : 0;
}

/** CSS class for an option-set chip; falls back to neutral. */
export function chipClass(schema: SchemaIndex, def: FieldDef, v: number | string): string {
  const slot = chipSlot(schema, v);
  return slot > 0 ? `chip chip-${slot}` : "chip";
}

export const BUNDLE_KINDS: ReadonlySet<string> = new Set(["enum", "state", "version", "build", "owned"]);
